import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import { Sparkles, ArrowRight, PenLine, ChevronRight, X } from './Icons';
import useHaptics from '../hooks/useHaptics';
import { apiGet, apiPost } from '../lib/api';
import { formatRecentDate } from '../lib/dateFormat';
import { useApplyQueue } from './Reflection/useApplyQueue';
import { useProposalHandlers } from './Reflection/useProposalHandlers';
import { renderThreadItem } from './Reflection/ThreadItem';
import { useTypewriter } from './Reflection/useTypewriter';
import {
  todayKey,
  draftKey,
  flashToast,
  renderGoldMarks,
  formatTimeWindow,
  useDebouncedCallback,
  FALLBACK_REPLY,
  THINKING_PHRASES,
} from './Reflection/helpers';
import './Reflection.css';

/* Rotating thinking-phrase hook. Cycles through THINKING_PHRASES at 800ms
   intervals while `active` is true. Resets to the first phrase whenever it
   re-activates so the user always sees a fresh sequence. */
function useThinkingPhrases(active) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!active) { setIndex(0); return undefined; }
    setIndex(0);
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % THINKING_PHRASES.length);
    }, 800);
    return () => clearInterval(id);
  }, [active]);
  return THINKING_PHRASES[index] || THINKING_PHRASES[0];
}

/* ---- Component ---- */
export default function Reflection({ userId }) {
  const navigate = useNavigate();
  const haptics  = useHaptics();

  const [body, setBody]             = useState('');
  // idle | writing | sending | thinking | revealing | conversation
  const [phase, setPhase]           = useState('idle');
  const [recent, setRecent]         = useState([]);
  const [thread, setThread]         = useState([]);
  const [pattern, setPattern]       = useState(null);
  const [sheetOpen, setSheetOpen]   = useState(false);
  const [findingPattern, setFinding] = useState(false);
  const [editingId, setEditingId]   = useState(null); // change_id currently in edit mode
  // id of the coach reply currently being typewritten (null when none)
  const [typewriterTargetId, setTypewriterTargetId] = useState(null);

  // Typewriter animation for coach replies
  const { progress: typewriterProgress, start: startTypewriter, stop: stopTypewriter, finishNow: finishTypewriterNow } = useTypewriter({ msPerChar: 18 });
  // Synchronous mirror of typewriterTargetId so onSend can detect an in-flight
  // animation without waiting for state to flush. Prevents truncated replies
  // when a second send fires before the first reply has finished typing.
  const typewriterTargetIdRef = useRef(null);

  // Rotating "thinking…" phrases (only animates while phase === 'thinking')
  const currentPhrase = useThinkingPhrases(phase === 'thinking');

  const textareaRef  = useRef(null);
  const idleTimerRef = useRef(null);
  const isSendingRef = useRef(false); // double-tap guard for onSend
  const refocusTimerRef = useRef(null); // refocus after send cleanup
  const threadRef = useRef([]); // sync ref for reading thread state in handlers

  // Unique ids for thread items — instance-scoped (avoids hot-reload edge cases
  // tied to a module-level counter).
  const idCounterRef = useRef(0);
  const nextId = useCallback(() => `t-${Date.now()}-${++idCounterRef.current}`, []);

  // Offline retry queues (failed ingests + failed applies).
  // Drains on mount + on `online`; exposes badge count + enqueue helpers.
  const { queueLength, queueFailed, queueFailedApply } = useApplyQueue(userId);

  // Proposal-card lifecycle handlers (confirm / delete / edit).
  const { onConfirm, onDelete, onEditOpen, onEditCancel, onEditSave } = useProposalHandlers({
    haptics,
    nextId,
    threadRef,
    setThread,
    setEditingId,
    queueFailedApply,
  });

  const inConversation = phase === 'conversation' || phase === 'revealing';
  const ready   = phase !== 'sending' && phase !== 'thinking' && phase !== 'revealing';
  const canSend = body.trim().length > 0 && ready;
  const textareaFading = phase === 'sending' || phase === 'thinking' || phase === 'revealing' || phase === 'conversation';

  /* ---------- mount: restore draft + load recents ---------- */
  useEffect(() => {
    // Logged-out edge: don't touch per-user storage
    if (userId == null) return;

    // 1. Restore draft
    try {
      const cached = localStorage.getItem(draftKey(userId));
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed.body === 'string') setBody(parsed.body);
      }
    } catch (_) { /* ignore */ }

    // 2. Load recent reflections
    let cancelled = false;
    (async () => {
      try {
        const r = await apiGet('/api/reflections?limit=5');
        if (!cancelled && r.ok) {
          const j = await r.json();
          const list = Array.isArray(j) ? j : (j && j.items) || [];
          setRecent(list);
        }
      } catch (_) { /* ignore */ }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- autosave (debounced 300ms) ---------- */
  const persistDraft = useDebouncedCallback((b) => {
    if (userId == null) return; // logged-out: don't share draft state across users
    try {
      localStorage.setItem(draftKey(userId), JSON.stringify({ body: b, t: Date.now() }));
    } catch (_) { /* ignore quota */ }
  }, 300);

  useEffect(() => { persistDraft(body); }, [body, persistDraft]);

  /* ---------- writing-mode idle timer ---------- */
  const bumpIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      if (document.activeElement !== textareaRef.current) {
        setPhase((p) => (p === 'writing' ? 'idle' : p));
      }
    }, 2500);
  }, []);

  useEffect(() => () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); }, []);

  /* ---------- close pattern sheet (declared before effects that depend on it) ---------- */
  const closeSheet = useCallback(() => { haptics.trigger('light'); setSheetOpen(false); }, [haptics]);

  /* ---------- ESC closes pattern sheet ---------- */
  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') closeSheet(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen, closeSheet]);

  /* ---------- keyboard-aware writing area (visualViewport) ---------- */
  useEffect(() => {
    if (!window.visualViewport) return;
    const onResize = () => {
      const heightDiff = window.innerHeight - window.visualViewport.height;
      document.querySelector('.ref-page')?.classList.toggle('is-keyboard-open', heightDiff > 100);
    };
    window.visualViewport.addEventListener('resize', onResize);
    window.visualViewport.addEventListener('scroll', onResize);
    return () => {
      window.visualViewport.removeEventListener('resize', onResize);
      window.visualViewport.removeEventListener('scroll', onResize);
    };
  }, []);

  /* Cleanup refocus timer on unmount */
  useEffect(() => () => {
    if (refocusTimerRef.current) clearTimeout(refocusTimerRef.current);
  }, []);

  /* Keep threadRef in sync with thread state for synchronous reads in handlers */
  useEffect(() => { threadRef.current = thread; }, [thread]);

  /* ---------- handlers ---------- */
  const onChange = (e) => {
    setBody(e.target.value);
    if (phase === 'idle') setPhase('writing');
    bumpIdleTimer();
  };

  const onFocus = () => { if (phase === 'idle') setPhase('writing'); bumpIdleTimer(); };
  const onBlur  = () => { bumpIdleTimer(); };

  /* ---------- send → ingest → push UserNote + CoachReply + ProposalCards ---------- */
  const onSend = async () => {
    if (!canSend) return;
    if (isSendingRef.current) return;
    isSendingRef.current = true;
    haptics.trigger('medium');
    const trimmedBody = body.trim();
    setPhase('sending');

    // Brief 'sending' fade window before transitioning to 'thinking'.
    // Runs in parallel with the network request so total wall-clock time
    // isn't penalised; we just guarantee at least the fade duration.
    const minSendingDelay = new Promise((resolve) => setTimeout(resolve, 250));

    // tz so backend can interpret relative time phrases.
    const tz = (() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) { return null; }
    })();

    const payload = { body: trimmedBody, date: todayKey(), mood: null, mode: 'today', tz };

    // Drop the user's prose from the surface as the page transitions to thinking.
    setBody('');
    try { localStorage.removeItem(draftKey(userId)); } catch (_) {}

    // Push the user note immediately so the diary shows what was just written.
    const ts = Date.now();
    const userItem = { id: nextId(), kind: 'user', body: trimmedBody, t: ts };
    setThread((prev) => [...prev, userItem]);

    // Wait for the fade-out, then enter the 'thinking' phase.
    await minSendingDelay;
    setPhase('thinking');

    let result    = null;
    let savedRemote = false;
    try {
      const r = await apiPost('/api/journal/ingest', payload);
      if (r.ok) {
        result = await r.json();
        savedRemote = true;
      } else {
        queueFailed(payload);
      }
    } catch (_) {
      queueFailed(payload);
    }

    let coachReplyText;
    let isOfflineNote = false;
    if (result && typeof result.coach_reply === 'string' && result.coach_reply.trim().length > 0) {
      coachReplyText = result.coach_reply;
    } else if (result && typeof result.summary === 'string' && result.summary.trim().length > 0) {
      coachReplyText = result.summary;
    } else if (!savedRemote) {
      coachReplyText = "Saved locally. I'll catch up when you're back online.";
      isOfflineNote = true;
    } else {
      coachReplyText = FALLBACK_REPLY;
    }
    const replyItem = isOfflineNote
      ? { id: nextId(), kind: 'note', text: coachReplyText, t: Date.now() }
      : { id: nextId(), kind: 'coach', reply: coachReplyText, t: Date.now() };

    const proposals = (result && Array.isArray(result.proposed_changes)) ? result.proposed_changes : [];
    // Dedupe by change_id against any proposals already present in the thread
    const existingChangeIds = new Set(
      threadRef.current
        .filter((x) => x.kind === 'proposal')
        .map((x) => x.change.change_id)
    );
    const proposalItems = proposals
      .filter((pc) => !existingChangeIds.has(pc.change_id))
      .map((pc) => ({
        id: nextId(),
        kind: 'proposal',
        change: pc,
      }));

    // Reveal: append the reply (and proposals) and start the typewriter (only for coach kind).
    setThread((prev) => [...prev, replyItem, ...proposalItems]);
    if (isOfflineNote) {
      setTypewriterTargetId(null);
      setPhase('conversation');
    } else {
      // If a previous typewriter is still running for a different target,
      // complete it instantly so that reply renders fully before we start anew.
      if (typewriterTargetIdRef.current && typewriterTargetIdRef.current !== replyItem.id) {
        finishTypewriterNow();
      }
      typewriterTargetIdRef.current = replyItem.id;
      setTypewriterTargetId(replyItem.id);
      setPhase('revealing');
      startTypewriter(coachReplyText.length, () => {
        setPhase('conversation');
        typewriterTargetIdRef.current = null;
        setTypewriterTargetId(null);
      });
    }

    flashToast(savedRemote ? 'Saved' : 'Saved locally', 'info');
    haptics.trigger('success');

    // Refocus textarea after slide animation
    if (refocusTimerRef.current) clearTimeout(refocusTimerRef.current);
    refocusTimerRef.current = setTimeout(() => {
      try { textareaRef.current && textareaRef.current.focus(); } catch (_) {}
      isSendingRef.current = false;
    }, 200);
  };

  /* ---------- thread reset / save & close ---------- */
  const resetThread = useCallback(() => {
    haptics.trigger('light');
    // Politely drop any still-open proposals (best-effort, fire-and-forget)
    const stillOpen = thread.filter((x) => x.kind === 'proposal');
    stillOpen.forEach((p) => {
      apiPost(`/api/pending-changes/${p.change.change_id}/reject`, {}).catch(() => {});
    });
    setThread([]);
    setEditingId(null);
    setBody('');
    setPhase('idle');
    stopTypewriter();
    typewriterTargetIdRef.current = null;
    setTypewriterTargetId(null);
    try { localStorage.removeItem(draftKey(userId)); } catch (_) {}
    requestAnimationFrame(() => { try { textareaRef.current && textareaRef.current.focus(); } catch (_) {} });
  }, [thread, haptics, userId, stopTypewriter]);

  const saveAndClose = useCallback(() => {
    haptics.trigger('light');
    const stillOpen = thread.filter((x) => x.kind === 'proposal');
    if (stillOpen.length > 0) {
      flashToast(`${stillOpen.length} proposal${stillOpen.length === 1 ? '' : 's'} dropped`);
      stillOpen.forEach((p) => {
        apiPost(`/api/pending-changes/${p.change.change_id}/reject`, {}).catch(() => {});
      });
    }
    setThread([]);
    setEditingId(null);
    setBody('');
    setPhase('idle');
    stopTypewriter();
    typewriterTargetIdRef.current = null;
    setTypewriterTargetId(null);
    try { localStorage.removeItem(draftKey(userId)); } catch (_) {}
    navigate('/app/today');
  }, [thread, haptics, userId, navigate, stopTypewriter]);

  /* ---------- pattern lookup ---------- */
  const onFindPattern = useCallback(async () => {
    if (findingPattern) return;
    setFinding(true);
    haptics.trigger('light');
    try {
      const r = await apiPost('/api/patterns/find', {});
      if (r.ok) {
        const j = await r.json();
        setPattern(j);
        setSheetOpen(true);
      } else if (r.status === 404) {
        setPattern({ statement: 'No pattern surfaced yet. Keep reflecting — they emerge with rhythm.', empty: true });
        setSheetOpen(true);
      } else {
        flashToast('Could not find a pattern');
      }
    } catch (_) {
      flashToast('Network error');
    } finally {
      setFinding(false);
    }
  }, [haptics, findingPattern]);

  /* ---------- derived ---------- */
  const dimmedClass    = phase === 'writing' ? 'is-dimmed' : '';
  const sendActive     = canSend;
  const timeWindowText = formatTimeWindow(pattern && pattern.time_window);

  /* ---------- render ---------- */
  // Logged-out edge: avoid sharing localStorage draft + queue state across anonymous users on the same device
  if (userId == null) {
    return (
      <div className="page ref-page">
        <PageHeader />
        <header className="ref-title-block">
          <h1 className="h-display">Reflection</h1>
          <p className="subtitle">Sign in to use Reflection.</p>
        </header>
      </div>
    );
  }

  return (
    <div className="page ref-page">
      <PageHeader />

      {/* TITLE BLOCK */}
      <header className="ref-title-block">
        <h1 className="h-display">Reflection</h1>
        <p className="subtitle">
          Pause. Reflect. See what&rsquo;s <span className="gold-mark">shaping</span> you.
        </p>
        {queueLength > 0 && (
          <div className="ref-sync-badge">{queueLength} pending sync</div>
        )}
      </header>

      <div className={`ref-stage ${inConversation ? 'is-conversation' : ''}`}>
        {/* CONVERSATION TOP BAR — only when thread has items */}
        {thread.length > 0 && (
          <div className="ref-thread-bar">
            <button type="button" className="btn-ghost ref-thread-bar-btn" onClick={resetThread}>
              New entry
            </button>
            <button type="button" className="btn-ghost ref-thread-bar-btn" onClick={saveAndClose}>
              Save &amp; close
            </button>
          </div>
        )}

        {/* PROMPT (hidden once we're in a conversation) */}
        {!inConversation && (
          <p className={`ref-prompt ${phase === 'writing' ? 'is-faded' : ''}`}>
            What mattered most today?
          </p>
        )}

        {/* THREAD — no aria-live here (would re-announce all items on every change) */}
        {thread.length > 0 && (
          <div className="ref-thread">
            {thread.map((item) => renderThreadItem(item, {
              editingId,
              onConfirm,
              onDelete,
              onEditOpen,
              onEditCancel,
              onEditSave,
              typewriterTargetId,
              typewriterProgress,
            }))}
          </div>
        )}

        {/* THINKING STATE — rotating phrases while we wait for the AI reply */}
        {phase === 'thinking' && (
          <div className="ref-thinking-state">
            <span className="ref-thinking-phrase">{currentPhrase}</span>
          </div>
        )}

        {/* Screen-reader-only live region: announces only the latest coach reply or note */}
        {(() => {
          const latest = [...thread].reverse().find((x) => x.kind === 'coach' || x.kind === 'note');
          if (!latest) return null;
          const text = latest.kind === 'coach' ? latest.reply : latest.text;
          return (
            <div aria-live="polite" aria-atomic="false" className="sr-only">
              {text}
            </div>
          );
        })()}

        {/* WRITING AREA — stays visible in conversation phase too */}
        <div
          className={`ref-writing-area ${inConversation ? 'is-followup' : ''}`}
          onClick={(e) => { if (e.target === e.currentTarget) textareaRef.current && textareaRef.current.focus(); }}
        >
          <PenLine size={16} color="var(--gold-500)" className="ref-pen" />
          <textarea
            ref={textareaRef}
            className={`ref-textarea ${textareaFading ? 'is-fading' : ''}`}
            placeholder={inConversation ? "Add to today's note…" : 'Start writing…'}
            value={body}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            rows={inConversation ? 4 : 10}
            aria-label="Today's reflection"
            disabled={phase === 'sending' || phase === 'thinking' || phase === 'revealing'}
            maxLength={10000}
          />
          {body.length > 8000 && (
            <div className="ref-char-count">{10000 - body.length} characters remaining</div>
          )}
          {/* Hint shown only on the very first empty entry */}
          {body.length === 0 && !inConversation && (
            <p className="ref-textarea-hint">
              Try: &ldquo;Worked on the strategy deck from 9 to 11, then a quick gym session, then meetings until 4.&rdquo;
            </p>
          )}
          <button
            type="button"
            className={`ref-send ${sendActive ? 'is-active' : ''}`}
            onClick={onSend}
            disabled={!sendActive}
            aria-label="Send reflection"
          >
            <ArrowRight size={18} />
          </button>
        </div>

        {/* FIND PATTERN */}
        <div className={`ref-find-pattern ${dimmedClass}`}>
          <button
            type="button"
            className="btn-primary ref-find-btn"
            onClick={onFindPattern}
            disabled={findingPattern}
          >
            <Sparkles size={16} />
            <span>{findingPattern ? 'Searching…' : 'Find pattern'}</span>
          </button>
        </div>

        {/* RECENT — true empty state */}
        <section className={`ref-recent ${dimmedClass}`} aria-label="Recent reflections">
          <div className="recent-head">
            <div className="eyebrow">RECENT REFLECTIONS</div>
            <button type="button" className="btn-ghost" onClick={() => navigate('/app/insights')}>
              View all
            </button>
          </div>
          {recent.length === 0 ? (
            <p className="ref-empty-recent">
              Nothing here yet — your reflections will appear after your first save.
            </p>
          ) : (
            <ul className="recent-list">
              {recent.slice(0, 3).map((r, i) => (
                <li key={r.reflection_id || `row-${i}`}>
                  <button
                    type="button"
                    className="recent-row"
                    aria-label={`Open reflection from ${r.date}`}
                    onClick={() => { haptics.trigger('light'); navigate('/app/insights'); }}
                  >
                    <div className="recent-meta">
                      <span className="recent-date mono">{formatRecentDate(r.date)}</span>
                    </div>
                    <div className="recent-summary">
                      {r.summary || (r.body ? r.body.slice(0, 120) : '—')}
                    </div>
                    <ChevronRight size={14} color="var(--ink-faint)" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* PATTERN BOTTOM SHEET */}
      {sheetOpen && (
        <div className="ref-sheet-backdrop" onClick={closeSheet} role="presentation">
          <div className="ref-sheet" role="dialog" aria-modal="true" aria-label="Pattern" onClick={(e) => e.stopPropagation()}>
            <div className="ref-sheet-handle" />
            <div className="ref-sheet-eyebrow eyebrow-gold">PATTERN</div>
            <p className="ref-sheet-statement">
              {renderGoldMarks(pattern && pattern.statement)}
            </p>
            {pattern && pattern.suggested_change && (
              <p className="ref-sheet-suggestion">{pattern.suggested_change}</p>
            )}
            {timeWindowText && (
              <div className="ref-sheet-meta mono">{timeWindowText}</div>
            )}
            <button type="button" className="ref-sheet-close icon-btn" aria-label="Close" onClick={closeSheet}>
              <X size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
